/**
 * The four calls the Create IPO Order screen makes.
 *
 * All of them are endpoints the panel already talks to, on the admin service
 * (`REACT_APP_ADMIN_SERVICE_BASE_URL`) — except the investor lookup, which is
 * on the user service, exactly as Create RFQ Order does it.
 *
 * `/ipo/ipo_ordercreate/` is the one new call. It is the same endpoint the user
 * platform's own IPO journey posts to (`smest-next/components/IPO/PaymentSteps.tsx`),
 * and it already goes through the admin service there, so nothing new had to be
 * wired to reach it.
 */

import {
  makeGETRequest,
  makeGETRequestOnUserService,
  makePOSTRequest,
} from "../../Api/api";
import type {
  IPODetailsResponse,
  IPOInvestorDetails,
  IPOOrderCreatePayload,
  IPOOrderCreateResponse,
  IPOSecurityMasterListRow,
} from "./interfaces";

export const getIPOErrorMessage = (
  error: any,
  fallback = "The request could not be completed."
): string => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return (
    data?.error ||
    data?.message ||
    data?.detail ||
    error?.message ||
    fallback
  );
};

/** Every issue an application can be raised against. */
export const fetchIPOSecurityMasters = async (): Promise<
  IPOSecurityMasterListRow[]
> => {
  const response: any = await makeGETRequest("/ipo/security_master/list/");
  return Array.isArray(response?.data) ? response.data : [];
};

/** One issue plus its series. Snake_case, uncamelised — see interfaces.ts. */
export const fetchIPODetails = async (
  ipoUuid: string
): Promise<IPODetailsResponse> => {
  const response: any = await makeGETRequest(
    `/ipo/details/${encodeURIComponent(ipoUuid)}`
  );
  const data = response?.data;
  if (!data?.ipo_security_master) {
    throw new Error("This IPO did not return its security master.");
  }
  return data as IPODetailsResponse;
};

/** The investor's PAN and demat, for prefill. Same call Create RFQ Order uses. */
export const fetchInvestorDetails = async (
  userUuid: string
): Promise<IPOInvestorDetails> => {
  const response: any = await makeGETRequestOnUserService(
    `user/get/${encodeURIComponent(userUuid)}/`
  );
  if (!response?.data?.uuid) {
    throw new Error("This investor's profile could not be loaded.");
  }
  return response.data as IPOInvestorDetails;
};

/**
 * Creates the application.
 *
 * Returns the raw body because the verdict lives inside it — a rejected bid
 * comes back on a 200 with an `errorcode` on the bid row. `readOrderOutcome`
 * in ./helpers is what interprets it.
 */
export const createIPOOrder = async (
  payload: IPOOrderCreatePayload
): Promise<IPOOrderCreateResponse> => {
  const response: any = await makePOSTRequest("/ipo/ipo_ordercreate/", payload);
  if (!response?.data) {
    throw new Error("The IPO order service returned an empty response.");
  }
  return response.data as IPOOrderCreateResponse;
};
